import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Sparkles,
  Phone,
  Mail,
  Calendar,
  TrendingUp,
  Target,
  Zap,
  ArrowLeft,
  Loader2,
  Clock,
  MessageSquare,
  Building,
  Briefcase,
  PhoneCall,
  MessageCircle,
  CheckCircle2,
  XCircle,
  DollarSign,
  FileText,
  User,
  Copy,
  Check,
  CalendarCheck,
  ArrowRight,
  Activity,
  BarChart3,
  Globe,
  Pencil,
  ChevronDown,
  ChevronUp,
  History,
  CalendarPlus,
  Bell,
  X,
  RefreshCw,
  UserCheck,
  Play,
  Rocket,
  UserPlus,
  CheckSquare,
  ClipboardList,
  Search,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { LeadFormModal } from "../../components/admin/LeadFormModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = "/api";

// Date/Time formatting helpers
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  
  // Handle ISO date strings (YYYY-MM-DD) to avoid timezone issues
  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  }
  
  // Handle MM/DD/YYYY format (already correct)
  if (typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    return dateString;
  }
  
  // Handle ISO datetime strings - parse and use UTC values to avoid timezone shift
  if (typeof dateString === 'string' && dateString.includes('T')) {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    // Use UTC methods to avoid timezone shift
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${month}/${day}/${year}`;
  }
  
  // Fallback for other date formats
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const formatTime = (timeString) => {
  if (!timeString) return "";
  
  // If already formatted with AM/PM, return as-is (clean up any double PM/AM)
  if (timeString.toLowerCase().includes('am') || timeString.toLowerCase().includes('pm')) {
    // Clean up any potential "PM PM" or "AM AM" issues
    return timeString.replace(/\s*(AM|PM)\s*(AM|PM)/gi, ' $1').trim();
  }
  
  // Handle HH:MM format (24-hour)
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  if (isNaN(hour)) return timeString;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Circular Progress Component
const CircularProgress = ({ value, size = 80, strokeWidth = 8, color = "#14A800" }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / 100) * circumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl font-bold text-white font-[Manrope]">{value}%</span>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, value, label, color = "#14A800", small = false }) => (
  <div className="bg-[#121212] p-4 rounded-xl border border-white/5 hover:border-white/10 transition-colors">
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className={`font-bold text-white font-[Manrope] ${small ? "text-lg" : "text-2xl"}`}>{value}</p>
        <p className="text-xs text-zinc-500">{label}</p>
      </div>
    </div>
  </div>
);

// Stage Style Helper
const getStageStyle = (stage) => {
  const styles = {
    "Lead": { bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/30", icon: "🎯" },
    "Opportunity": { bg: "bg-[#14A800]/20", text: "text-[#14A800]", border: "border-[#14A800]/30", icon: "🔥" },
    "Contracts & Payments": { bg: "bg-amber-500/20", text: "text-amber-400", border: "border-amber-500/30", icon: "📝" },
    "Implementation": { bg: "bg-purple-500/20", text: "text-purple-400", border: "border-purple-500/30", icon: "🚀" },
  };
  return styles[stage] || styles["Lead"];
};

// Status Style Helper
const getStatusStyle = (status) => {
  const styles = {
    "New": { bg: "bg-blue-500/20", text: "text-blue-400" },
    "Contacted": { bg: "bg-purple-500/20", text: "text-purple-400" },
    "Qualified": { bg: "bg-green-500/20", text: "text-green-400" },
    "Converted": { bg: "bg-[#14A800]/20", text: "text-[#14A800]" },
  };
  return styles[status] || styles["New"];
};

// Sentiment Badge
const SentimentBadge = ({ sentiment }) => {
  const styles = {
    "Positive": { bg: "bg-green-500/20", text: "text-green-400", icon: "😊" },
    "Negative": { bg: "bg-red-500/20", text: "text-red-400", icon: "😞" },
    "Neutral": { bg: "bg-zinc-500/20", text: "text-zinc-400", icon: "😐" },
    "Unknown": { bg: "bg-zinc-500/20", text: "text-zinc-400", icon: "❓" },
  };
  const style = styles[sentiment] || styles["Unknown"];
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
      <span>{style.icon}</span>
      {sentiment || "Unknown"}
    </span>
  );
};

export default function LeadDetails() {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [callHistory, setCallHistory] = useState(null);
  const [emailHistory, setEmailHistory] = useState(null);
  const [textHistory, setTextHistory] = useState(null);
  const [appointmentHistory, setAppointmentHistory] = useState(null);
  const [aiCallLoading, setAiCallLoading] = useState(false);
  const [aiContent, setAiContent] = useState({ type: null, content: "", loading: false });
  const [copied, setCopied] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [appointmentNotes, setAppointmentNotes] = useState("");
  const [savingAppointment, setSavingAppointment] = useState(false);
  const [cancellingAppointment, setCancellingAppointment] = useState(null);
  const [showAppointmentReminder, setShowAppointmentReminder] = useState(false);
  const [appointmentReminderDismissed, setAppointmentReminderDismissed] = useState(false);
  
  // Expandable sections state
  const [expandedSections, setExpandedSections] = useState({
    calls: false,
    emails: false,
    texts: false,
    appointments: false
  });
  
  // Team members for originator selection (admin only)
  const [teamMembers, setTeamMembers] = useState([]);
  const [showOriginatorEdit, setShowOriginatorEdit] = useState(false);
  const [savingOriginator, setSavingOriginator] = useState(false);
  
  // Activity timeline for Sales role
  const [activityTimeline, setActivityTimeline] = useState({ activities: [], summary: {} });
  const [activityLoading, setActivityLoading] = useState(false);
  
  const token = localStorage.getItem("admin_token");
  
  // Get user role and check AI Call permission
  const userRole = localStorage.getItem("admin_role") || "admin";
  const hasAiCallPermission = ["admin", "sales", "marketing", "product_manager"].includes(userRole);
  const isAdmin = userRole === "admin";

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchLeadDetails();
    // Fetch team members for assignment dropdowns (all roles need this)
    fetchTeamMembers();
    // Fetch activity timeline for sales role
    if (userRole === "sales") {
      fetchActivityTimeline();
    }
  }, [leadId, token]);

  const fetchActivityTimeline = async () => {
    setActivityLoading(true);
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/activity-timeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setActivityTimeline(data);
      }
    } catch (error) {
      console.error("Error fetching activity timeline:", error);
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API}/admin/team-members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data.team_members || []);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };

  const handleUpdateOriginator = async (originatorId) => {
    setSavingOriginator(true);
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/originator`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ originated_by: originatorId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setLead(prev => ({
          ...prev,
          originated_by: data.originated_by,
          originated_by_name: data.originated_by_name
        }));
        toast.success("Originator updated successfully");
        setShowOriginatorEdit(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update originator");
      }
    } catch (error) {
      console.error("Error updating originator:", error);
      toast.error("Failed to update originator");
    } finally {
      setSavingOriginator(false);
    }
  };

  const fetchLeadDetails = async () => {
    setLoading(true);
    try {
      // Fetch all leads and find the one we need
      const leadsResp = await fetch(`${API}/admin/leads?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let foundLead = null;
      if (leadsResp.ok) {
        const data = await leadsResp.json();
        foundLead = data.leads?.find(l => l.lead_id === leadId);
        if (foundLead) {
          setLead(foundLead);
          
          // Check for upcoming appointment and show reminder
          if (foundLead.appointment_date && !appointmentReminderDismissed) {
            const appointmentDateTime = new Date(foundLead.appointment_date);
            const now = new Date();
            const daysUntil = Math.ceil((appointmentDateTime - now) / (1000 * 60 * 60 * 24));
            
            // Show reminder if appointment is within 7 days
            if (daysUntil >= 0 && daysUntil <= 7) {
              setShowAppointmentReminder(true);
            }
          }
        } else {
          toast.error("Lead not found");
          navigate("/admin/leads");
          return;
        }
      }
      
      // Fetch call history
      const callResp = await fetch(`${API}/admin/leads/${leadId}/call-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (callResp.ok) {
        const callData = await callResp.json();
        setCallHistory(callData);
      }
      
      // Fetch email history
      const emailResp = await fetch(`${API}/admin/leads/${leadId}/email-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (emailResp.ok) {
        const emailData = await emailResp.json();
        setEmailHistory(emailData);
      }
      
      // Fetch text history
      const textResp = await fetch(`${API}/admin/leads/${leadId}/text-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (textResp.ok) {
        const textData = await textResp.json();
        setTextHistory(textData);
      }
      
      // Fetch appointment history
      const apptResp = await fetch(`${API}/admin/leads/${leadId}/appointment-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (apptResp.ok) {
        const apptData = await apptResp.json();
        setAppointmentHistory(apptData);
      }
    } catch (error) {
      toast.error("Failed to fetch lead details");
    } finally {
      setLoading(false);
    }
  };

  // AI Call handler
  const handleAICall = async () => {
    const phoneNumber = lead?.mobile_phone || lead?.company_phone;
    if (!phoneNumber) {
      toast.error("This lead has no phone number");
      return;
    }
    
    setAiCallLoading(true);
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/call`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`AI call initiated to ${lead.first_name}!`, {
          description: `Calling ${data.to_number}...`
        });
        // Refresh call history
        setTimeout(fetchLeadDetails, 2000);
      } else {
        const error = await response.json();
        if (response.status === 503) {
          toast.error("Retell AI not configured", {
            description: "Add RETELL_API_KEY, RETELL_FROM_NUMBER, and RETELL_AGENT_ID to enable AI calls"
          });
        } else {
          toast.error(error.detail || "Failed to initiate AI call");
        }
      }
    } catch (error) {
      toast.error("Network error - please try again");
    } finally {
      setAiCallLoading(false);
    }
  };

  // Generate AI content
  const handleGenerateContent = async (type) => {
    setAiContent({ type, content: "", loading: true });
    
    try {
      let endpoint;
      if (type === "email") {
        endpoint = `${API}/admin/crm/draft-email?lead_id=${leadId}`;
      } else if (type === "text") {
        endpoint = `${API}/admin/crm/draft-text?lead_id=${leadId}`;
      } else if (type === "appointment") {
        endpoint = `${API}/admin/crm/suggest-appointment?lead_id=${leadId}`;
      } else {
        endpoint = `${API}/admin/crm/draft-call-script?lead_id=${leadId}`;
      }
        
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Handle appointment response
        if (type === "appointment") {
          if (data.appointment_set) {
            setAiContent({ type: null, content: "", loading: false });
            toast.success("Opportunity Appointment Set!");
            fetchLeadDetails(); // Refresh to show new appointment
          } else {
            const content = data.suggestion || data.message || "No appointment suggestion available";
            setAiContent({ type, content, loading: false });
          }
        } else {
          const content = data.email_draft || data.text_draft || data.call_script || "";
          setAiContent({ type, content, loading: false });
        }
        
        // Refresh history after generating content
        if (type === "email") {
          const emailResp = await fetch(`${API}/admin/leads/${leadId}/email-history`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (emailResp.ok) {
            const emailData = await emailResp.json();
            setEmailHistory(emailData);
          }
        } else if (type === "text") {
          const textResp = await fetch(`${API}/admin/leads/${leadId}/text-history`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (textResp.ok) {
            const textData = await textResp.json();
            setTextHistory(textData);
          }
        } else if (type === "appointment") {
          const apptResp = await fetch(`${API}/admin/leads/${leadId}/appointment-history`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (apptResp.ok) {
            const apptData = await apptResp.json();
            setAppointmentHistory(apptData);
          }
        }
      } else {
        toast.error(`Failed to generate ${type} content`);
        setAiContent({ type: null, content: "", loading: false });
      }
    } catch (error) {
      toast.error(`Failed to generate ${type} content`);
      setAiContent({ type: null, content: "", loading: false });
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSaveAppointment = async () => {
    if (!appointmentDate) {
      toast.error("Please select a date");
      return;
    }
    
    setSavingAppointment(true);
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/appointment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          appointment_date: appointmentDate,
          appointment_time: appointmentTime || null,
          notes: appointmentNotes || ""
        })
      });
      
      if (response.ok) {
        setShowAppointmentForm(false);
        setAppointmentDate("");
        setAppointmentTime("");
        setAppointmentNotes("");
        toast.success("Appointment scheduled!");
        fetchLeadDetails();
      } else {
        toast.error("Failed to save appointment");
      }
    } catch (error) {
      toast.error("Failed to save appointment");
    } finally {
      setSavingAppointment(false);
    }
  };

  const handleCancelAppointment = async (appointmentIndex) => {
    setCancellingAppointment(appointmentIndex);
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/appointment/${appointmentIndex}/cancel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        toast.success("Appointment cancelled");
        fetchLeadDetails();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to cancel appointment");
      }
    } catch (error) {
      toast.error("Failed to cancel appointment");
    } finally {
      setCancellingAppointment(null);
    }
  };

  const handleStageChange = async (newStage) => {
    const currentStage = lead?.stage || "Lead";
    const isConvertingToOpportunity = currentStage === "Lead" && newStage === "Opportunity";
    
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ stage: newStage })
      });
      
      if (response.ok) {
        toast.success(`Lead moved to ${newStage}!`);
        
        // If Sales Rep converted Lead to Opportunity, redirect to dashboard
        if (userRole === "sales" && isConvertingToOpportunity) {
          toast.success("Lead converted to Opportunity! Redirecting to dashboard...");
          navigate("/admin/crm");
          return;
        }
        
        fetchLeadDetails();
      } else {
        toast.error("Failed to update stage");
      }
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#14A800] animate-spin" />
      </div>
    );
  }

  if (!lead) {
    return null;
  }

  const stageStyle = getStageStyle(lead.stage || "Lead");
  const statusStyle = getStatusStyle(lead.status || "New");
  const priority = lead.priority || { score: 0, priority_level: "low", priority_label: "Low", factors: [], tasks: [] };
  const hasAppointment = !!lead.appointment_date;

  return (
    <div className="min-h-screen bg-[#09090b] p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/admin/leads")}
            className="flex items-center gap-2 text-zinc-500 hover:text-[#14A800] transition-colors group"
            data-testid="back-to-leads"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm">
              {userRole === "product_manager" ? "Back to Opportunity Management" : "Back to Lead Management"}
            </span>
          </button>
          
          <Button 
            onClick={() => setShowEditModal(true)}
            variant="outline"
            className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            data-testid="edit-lead-btn"
          >
            <Pencil className="w-4 h-4 mr-2" />
            Edit Lead
          </Button>
        </div>

        {/* Lead Header Card */}
        <div className="bg-[#121212] rounded-2xl border border-white/5 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#14A800]/20 to-[#14A800]/5 flex items-center justify-center border border-[#14A800]/20">
                <span className="text-3xl font-bold text-[#14A800]">
                  {(lead.first_name?.[0] || "?")}{(lead.last_name?.[0] || "")}
                </span>
              </div>
              
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-white font-[Manrope]">
                    {lead.first_name} {lead.last_name}
                  </h1>
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm font-bold">
                    <Zap className="w-4 h-4" />
                    {priority.score}
                  </div>
                </div>
                
                <div className="flex items-center gap-3 text-zinc-400">
                  {lead.job_title && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      {lead.job_title}
                    </span>
                  )}
                  {lead.company_name && (
                    <span className="flex items-center gap-1">
                      <Building className="w-4 h-4" />
                      {lead.company_name}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-2 mt-3">
                  <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${stageStyle.bg} ${stageStyle.text} ${stageStyle.border} border`}>
                    <span>{stageStyle.icon}</span>
                    {lead.stage || "Lead"}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                    {lead.status || "New"}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-zinc-500/20 text-zinc-400">
                    {lead.source || "inbound"}
                  </span>
                </div>
                
                {/* Assignment Fields - Clean, simple design */}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {/* LEAD STAGE: Show "Sales Rep:" field with dropdown */}
                  {lead.stage === "Lead" && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <UserCheck className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs text-emerald-400">Sales Rep:</span>
                      {(isAdmin || (userRole === "sales" && !lead.assigned_to)) ? (
                        <Select
                          value={lead.assigned_to || "unassigned"}
                          onValueChange={async (value) => {
                            try {
                              const response = await fetch(`${API}/admin/leads/${leadId}/assign`, {
                                method: "POST",
                                headers: {
                                  Authorization: `Bearer ${token}`,
                                  "Content-Type": "application/json"
                                },
                                body: JSON.stringify({ assigned_to: value === "unassigned" ? null : value })
                              });
                              if (response.ok) {
                                const data = await response.json();
                                setLead(prev => ({
                                  ...prev,
                                  assigned_to: data.assigned_to,
                                  assigned_to_name: data.assignee_name
                                }));
                                toast.success(data.assignee_name ? `Assigned to ${data.assignee_name}` : "Unassigned");
                              } else {
                                const error = await response.json();
                                toast.error(error.detail || "Failed to assign");
                              }
                            } catch (error) {
                              toast.error("Failed to assign");
                            }
                          }}
                        >
                          <SelectTrigger className="w-[180px] h-7 text-xs bg-zinc-800 border-zinc-700">
                            <SelectValue placeholder="Select Sales Rep" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="unassigned" className="text-zinc-400">Unassigned</SelectItem>
                            {/* Sales Rep can only assign to themselves, Admin can assign to any Sales Rep */}
                            {userRole === "sales" ? (
                              teamMembers
                                .filter(m => m.team_member_id === localStorage.getItem("admin_team_member_id"))
                                .map(member => (
                                  <SelectItem key={member.team_member_id} value={member.team_member_id} className="text-white">
                                    {member.name} (Me)
                                  </SelectItem>
                                ))
                            ) : (
                              teamMembers.filter(m => m.role === "sales").map(member => (
                                <SelectItem key={member.team_member_id} value={member.team_member_id} className="text-white">
                                  {member.name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-xs text-white font-medium">
                          {lead.assigned_to_name || teamMembers.find(m => m.team_member_id === lead.assigned_to)?.name || "Unassigned"}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* OPPORTUNITY+ STAGES: Show "Sales Rep:" (static) + "Product Manager:" (dropdown) */}
                  {(lead.stage === "Opportunity" || lead.stage === "Contracts & Payments" || lead.stage === "Implementation") && (
                    <>
                      {/* Sales Rep who claimed the lead (static display) */}
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                        <UserCheck className="w-4 h-4 text-emerald-400" />
                        <span className="text-xs text-emerald-400">Sales Rep:</span>
                        <span className="text-xs text-white font-medium">
                          {lead.sales_rep_name || lead.originated_by_name || "Not assigned"}
                        </span>
                      </div>
                      
                      {/* Product Manager assignment */}
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
                        <User className="w-4 h-4 text-blue-400" />
                        <span className="text-xs text-blue-400">Product Manager:</span>
                        {(isAdmin || (userRole === "product_manager" && !lead.assigned_to)) ? (
                          <Select
                            value={lead.assigned_to || "unassigned"}
                            onValueChange={async (value) => {
                              try {
                                const response = await fetch(`${API}/admin/leads/${leadId}/assign`, {
                                  method: "POST",
                                  headers: {
                                    Authorization: `Bearer ${token}`,
                                    "Content-Type": "application/json"
                                  },
                                  body: JSON.stringify({ assigned_to: value === "unassigned" ? null : value })
                                });
                                if (response.ok) {
                                  const data = await response.json();
                                  setLead(prev => ({
                                    ...prev,
                                    assigned_to: data.assigned_to,
                                    assigned_to_name: data.assignee_name
                                  }));
                                  toast.success(data.assignee_name ? `Assigned to ${data.assignee_name}` : "Unassigned");
                                } else {
                                  const error = await response.json();
                                  toast.error(error.detail || "Failed to assign");
                                }
                              } catch (error) {
                                toast.error("Failed to assign");
                              }
                            }}
                          >
                            <SelectTrigger className="w-[180px] h-7 text-xs bg-zinc-800 border-zinc-700">
                              <SelectValue placeholder="Select PM" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              <SelectItem value="unassigned" className="text-zinc-400">Unassigned</SelectItem>
                              {/* PM can only assign to themselves, Admin can assign to any PM */}
                              {userRole === "product_manager" ? (
                                teamMembers
                                  .filter(m => m.team_member_id === localStorage.getItem("admin_team_member_id"))
                                  .map(member => (
                                    <SelectItem key={member.team_member_id} value={member.team_member_id} className="text-white">
                                      {member.name} (Me)
                                    </SelectItem>
                                  ))
                              ) : (
                                teamMembers.filter(m => m.role === "product_manager").map(member => (
                                  <SelectItem key={member.team_member_id} value={member.team_member_id} className="text-white">
                                    {member.name}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-white font-medium">
                            {lead.assigned_to_name || "Unassigned"}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Pipeline Health */}
            <div className="text-center">
              <CircularProgress value={priority.score} size={90} />
              <p className="text-xs text-zinc-500 mt-2">Lead Score</p>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Contact & AI Actions */}
          <div className="col-span-8 space-y-6">
            {/* Contact Information */}
            <div className="bg-[#121212] rounded-2xl border border-white/5 p-6">
              <h2 className="text-lg font-bold text-white font-[Manrope] mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#14A800]" />
                Contact Information
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-[#0a0a0a] rounded-xl border border-white/5">
                  <p className="text-xs text-zinc-500 mb-1">Email</p>
                  <p className="text-white font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4 text-blue-400" />
                    {lead.email || "Not provided"}
                  </p>
                </div>
                <div className="p-4 bg-[#0a0a0a] rounded-xl border border-white/5">
                  <p className="text-xs text-zinc-500 mb-1">Mobile Phone</p>
                  <p className="text-white font-medium flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-400" />
                    {lead.mobile_phone || "Not provided"}
                  </p>
                </div>
                <div className="p-4 bg-[#0a0a0a] rounded-xl border border-white/5">
                  <p className="text-xs text-zinc-500 mb-1">Company Phone</p>
                  <p className="text-white font-medium flex items-center gap-2">
                    <Phone className="w-4 h-4 text-purple-400" />
                    {lead.company_phone || "Not provided"}
                  </p>
                </div>
                <div className="p-4 bg-[#0a0a0a] rounded-xl border border-white/5">
                  <p className="text-xs text-zinc-500 mb-1">Industry</p>
                  <p className="text-white font-medium flex items-center gap-2">
                    <Building className="w-4 h-4 text-cyan-400" />
                    {lead.industry || "Not specified"}
                  </p>
                </div>
              </div>
              
              {lead.notes && (
                <div className="mt-4 p-4 bg-[#0a0a0a] rounded-xl border border-white/5">
                  <p className="text-xs text-zinc-500 mb-1">Notes</p>
                  <p className="text-zinc-300 text-sm">{lead.notes}</p>
                </div>
              )}
            </div>

            {/* ZOEY AI Actions - Redesigned */}
            <div className="bg-gradient-to-br from-[#14A800]/5 to-[#121212] rounded-2xl border border-[#14A800]/20 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-white font-[Manrope] flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#14A800]" />
                  ZOEY AI Actions
                </h2>
                <span className="text-xs text-zinc-500 bg-[#14A800]/10 px-2 py-1 rounded-full">
                  Click to open action panel
                </span>
              </div>
              
              {/* Action Cards - Open Modals */}
              <div className={`grid ${hasAiCallPermission ? 'grid-cols-4' : 'grid-cols-3'} gap-4`}>
                {/* AI Call Card */}
                {hasAiCallPermission && (
                  <button
                    onClick={() => navigate(`/admin/leads/${leadId}/call`)}
                    className="group relative flex flex-col items-center gap-3 p-6 bg-[#0a0a0a] rounded-xl border-2 border-purple-500/20 hover:border-purple-500/50 hover:bg-purple-500/5 transition-all"
                    data-testid="ai-call-btn"
                  >
                    <div className="absolute top-2 right-2">
                      <Play className="w-3 h-3 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="p-4 rounded-2xl bg-purple-500/20 group-hover:bg-purple-500/30 transition-colors">
                      <PhoneCall className="w-7 h-7 text-purple-400" />
                    </div>
                    <div className="text-center">
                      <p className="text-white font-semibold">AI Call</p>
                      <p className="text-xs text-zinc-500 mt-1">Voice outreach</p>
                    </div>
                    {callHistory?.total_calls_made > 0 && (
                      <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                        {callHistory.total_calls_made}
                      </span>
                    )}
                  </button>
                )}
                
                {/* AI Email Card */}
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/email`)}
                  className="group relative flex flex-col items-center gap-3 p-6 bg-[#0a0a0a] rounded-xl border-2 border-blue-500/20 hover:border-blue-500/50 hover:bg-blue-500/5 transition-all"
                  data-testid="ai-email-btn"
                >
                  <div className="absolute top-2 right-2">
                    <Play className="w-3 h-3 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-4 rounded-2xl bg-blue-500/20 group-hover:bg-blue-500/30 transition-colors">
                    <Mail className="w-7 h-7 text-blue-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-white font-semibold">AI Email</p>
                    <p className="text-xs text-zinc-500 mt-1">Draft & send</p>
                  </div>
                  {emailHistory?.total_emails_generated > 0 && (
                    <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {emailHistory.total_emails_generated}
                    </span>
                  )}
                </button>
                
                {/* AI Text Card */}
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/text`)}
                  className="group relative flex flex-col items-center gap-3 p-6 bg-[#0a0a0a] rounded-xl border-2 border-green-500/20 hover:border-green-500/50 hover:bg-green-500/5 transition-all"
                  data-testid="ai-text-btn"
                >
                  <div className="absolute top-2 right-2">
                    <Play className="w-3 h-3 text-green-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-4 rounded-2xl bg-green-500/20 group-hover:bg-green-500/30 transition-colors">
                    <MessageCircle className="w-7 h-7 text-green-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-white font-semibold">AI Text</p>
                    <p className="text-xs text-zinc-500 mt-1">SMS message</p>
                  </div>
                  {textHistory?.total_texts_generated > 0 && (
                    <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {textHistory.total_texts_generated}
                    </span>
                  )}
                </button>
                
                {/* AI Appointment Card */}
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/appointment`)}
                  className="group relative flex flex-col items-center gap-3 p-6 bg-[#0a0a0a] rounded-xl border-2 border-amber-500/20 hover:border-amber-500/50 hover:bg-amber-500/5 transition-all"
                  data-testid="ai-appointment-btn"
                >
                  <div className="absolute top-2 right-2">
                    <Play className="w-3 h-3 text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-4 rounded-2xl bg-amber-500/20 group-hover:bg-amber-500/30 transition-colors">
                    <CalendarPlus className="w-7 h-7 text-amber-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-white font-semibold">Schedule</p>
                    <p className="text-xs text-zinc-500 mt-1">Appointment</p>
                  </div>
                  {appointmentHistory?.total_appointments > 0 && (
                    <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {appointmentHistory.total_appointments}
                    </span>
                  )}
                </button>
              </div>
              
              {/* Quick Stats Row */}
              <div className="mt-4 pt-4 border-t border-white/5">
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <span>{callHistory?.total_calls_made || 0} calls • {emailHistory?.total_emails_generated || 0} emails • {textHistory?.total_texts_generated || 0} texts</span>
                  <span className="text-[#14A800]">All actions are AI-powered</span>
                </div>
              </div>
            </div>

            {/* Lead Workflow Card - Only shown for Lead stage */}
            {lead?.stage === "Lead" && (
              <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-2xl border border-emerald-500/30 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500">
                      <UserPlus className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white font-[Manrope]">AI Lead Workflow</h2>
                      <p className="text-xs text-zinc-400">Qualify and nurture this lead</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/admin/leads/${leadId}/lead-workflow`)}
                    className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30"
                    data-testid="lead-workflow-btn"
                  >
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Open Workflow
                  </Button>
                </div>
                
                {/* Quick Lead Workflow Stats */}
                {lead?.lead_workflow ? (
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-white">
                        {lead.lead_workflow.phases?.filter(p => p.status === "completed").length || 0}
                      </p>
                      <p className="text-xs text-zinc-500">Phases Done</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-white">
                        {lead.lead_workflow.lead_score || 0}%
                      </p>
                      <p className="text-xs text-zinc-500">Lead Score</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className={`text-2xl font-bold ${
                        lead.lead_workflow.lead_temperature === "hot" ? "text-red-400" :
                        lead.lead_workflow.lead_temperature === "warm" ? "text-orange-400" : "text-blue-400"
                      }`}>
                        {lead.lead_workflow.lead_temperature === "hot" ? "🔥" :
                         lead.lead_workflow.lead_temperature === "warm" ? "☀️" : "❄️"}
                      </p>
                      <p className="text-xs text-zinc-500">Temperature</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className={`text-2xl font-bold ${
                        lead.lead_workflow.health === "on_track" ? "text-green-400" :
                        lead.lead_workflow.health === "at_risk" ? "text-yellow-400" : "text-red-400"
                      }`}>
                        {lead.lead_workflow.health === "on_track" ? "🟢" :
                         lead.lead_workflow.health === "at_risk" ? "🟡" : "🔴"}
                      </p>
                      <p className="text-xs text-zinc-500">Health</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 bg-zinc-900/30 rounded-xl">
                    <p className="text-sm text-zinc-400">Click "Open Workflow" to start qualifying this lead</p>
                  </div>
                )}
              </div>
            )}

            {/* Manual Lead Workflow Card - Only shown for Lead stage */}
            {lead?.stage === "Lead" && (
              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl border border-blue-500/30 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500">
                      <ClipboardList className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white font-[Manrope]">Manual Lead Workflow</h2>
                      <p className="text-xs text-zinc-400">Traditional phase-based qualification</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/admin/leads/${leadId}/manual-workflow`)}
                    className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30"
                    data-testid="manual-workflow-btn"
                  >
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Open Workflow
                  </Button>
                </div>
                
                {/* Manual Workflow Phases Preview */}
                <div className="grid grid-cols-3 gap-3">
                  <div className={`rounded-xl p-3 text-center border ${
                    lead?.manual_workflow?.phases?.[0]?.status === "completed" 
                      ? "bg-green-500/10 border-green-500/30" 
                      : lead?.manual_workflow?.phases?.[0]?.status === "in_progress"
                      ? "bg-blue-500/10 border-blue-500/30"
                      : "bg-zinc-900/50 border-zinc-700/30"
                  }`}>
                    <MessageCircle className={`w-5 h-5 mx-auto mb-1 ${
                      lead?.manual_workflow?.phases?.[0]?.status === "completed" ? "text-green-400" :
                      lead?.manual_workflow?.phases?.[0]?.status === "in_progress" ? "text-blue-400" : "text-zinc-500"
                    }`} />
                    <p className="text-xs text-zinc-300">Conversation</p>
                  </div>
                  <div className={`rounded-xl p-3 text-center border ${
                    lead?.manual_workflow?.phases?.[1]?.status === "completed" 
                      ? "bg-green-500/10 border-green-500/30" 
                      : lead?.manual_workflow?.phases?.[1]?.status === "in_progress"
                      ? "bg-blue-500/10 border-blue-500/30"
                      : "bg-zinc-900/50 border-zinc-700/30"
                  }`}>
                    <Search className={`w-5 h-5 mx-auto mb-1 ${
                      lead?.manual_workflow?.phases?.[1]?.status === "completed" ? "text-green-400" :
                      lead?.manual_workflow?.phases?.[1]?.status === "in_progress" ? "text-blue-400" : "text-zinc-500"
                    }`} />
                    <p className="text-xs text-zinc-300">Discovery</p>
                  </div>
                  <div className={`rounded-xl p-3 text-center border ${
                    lead?.manual_workflow?.phases?.[2]?.status === "completed" 
                      ? "bg-green-500/10 border-green-500/30" 
                      : lead?.manual_workflow?.phases?.[2]?.status === "in_progress"
                      ? "bg-blue-500/10 border-blue-500/30"
                      : "bg-zinc-900/50 border-zinc-700/30"
                  }`}>
                    <Target className={`w-5 h-5 mx-auto mb-1 ${
                      lead?.manual_workflow?.phases?.[2]?.status === "completed" ? "text-green-400" :
                      lead?.manual_workflow?.phases?.[2]?.status === "in_progress" ? "text-blue-400" : "text-zinc-500"
                    }`} />
                    <p className="text-xs text-zinc-300">Qualification</p>
                  </div>
                </div>
              </div>
            )}

            {/* Implementation Workflow Card - Only shown for Implementation stage */}
            {lead?.stage === "Implementation" && (
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl border border-purple-500/30 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
                      <Rocket className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white font-[Manrope]">Implementation Workflow</h2>
                      <p className="text-xs text-zinc-400">Manage project phases and tasks</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/admin/leads/${leadId}/implementation`)}
                    className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30"
                    data-testid="implementation-workflow-btn"
                  >
                    <Rocket className="w-4 h-4 mr-2" />
                    Open Workflow
                  </Button>
                </div>
                
                {/* Quick Implementation Stats */}
                {lead?.implementation ? (
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-white">
                        {lead.implementation.phases?.filter(p => p.status === "completed").length || 0}
                      </p>
                      <p className="text-xs text-zinc-500">Phases Done</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-white">
                        {lead.implementation.phases?.length || 7}
                      </p>
                      <p className="text-xs text-zinc-500">Total Phases</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-white">
                        {(() => {
                          const totalTasks = lead.implementation.phases?.reduce((sum, p) => sum + (p.tasks?.length || 0), 0) || 0;
                          const completedTasks = lead.implementation.phases?.reduce((sum, p) => sum + (p.tasks?.filter(t => t.completed).length || 0), 0) || 0;
                          return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
                        })()}%
                      </p>
                      <p className="text-xs text-zinc-500">Progress</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className={`text-2xl font-bold ${
                        lead.implementation.health === "on_track" ? "text-green-400" :
                        lead.implementation.health === "at_risk" ? "text-yellow-400" : "text-red-400"
                      }`}>
                        {lead.implementation.health === "on_track" ? "🟢" :
                         lead.implementation.health === "at_risk" ? "🟡" : "🔴"}
                      </p>
                      <p className="text-xs text-zinc-500">Health</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 bg-zinc-900/30 rounded-xl">
                    <p className="text-sm text-zinc-400">Click "Open Workflow" to start implementation tracking</p>
                  </div>
                )}
              </div>
            )}

            {/* Opportunity Workflow Card - Only shown for Opportunity stage */}
            {lead?.stage === "Opportunity" && (
              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl border border-blue-500/30 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500">
                      <Target className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white font-[Manrope]">Opportunity Workflow</h2>
                      <p className="text-xs text-zinc-400">Track sales process and deal progress</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/admin/leads/${leadId}/opportunity-workflow`)}
                    className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30"
                    data-testid="opportunity-workflow-btn"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Open Workflow
                  </Button>
                </div>
                
                {lead?.opportunity_workflow ? (
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-white">
                        {lead.opportunity_workflow.phases?.filter(p => p.status === "completed").length || 0}
                      </p>
                      <p className="text-xs text-zinc-500">Phases Done</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-400">
                        {lead.opportunity_workflow.probability || 20}%
                      </p>
                      <p className="text-xs text-zinc-500">Win Prob</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-white">
                        {lead.opportunity_workflow.deal_value || "-"}
                      </p>
                      <p className="text-xs text-zinc-500">Deal Value</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className={`text-2xl font-bold ${
                        lead.opportunity_workflow.health === "on_track" ? "text-green-400" :
                        lead.opportunity_workflow.health === "at_risk" ? "text-yellow-400" : "text-red-400"
                      }`}>
                        {lead.opportunity_workflow.health === "on_track" ? "🟢" :
                         lead.opportunity_workflow.health === "at_risk" ? "🟡" : "🔴"}
                      </p>
                      <p className="text-xs text-zinc-500">Health</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 bg-zinc-900/30 rounded-xl">
                    <p className="text-sm text-zinc-400">Click "Open Workflow" to start tracking this opportunity</p>
                  </div>
                )}
              </div>
            )}

            {/* Contracts Workflow Card - Only shown for Contracts stage */}
            {(lead?.stage === "Contracts" || lead?.stage === "Contracts & Payments") && (
              <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-2xl border border-orange-500/30 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-orange-500 to-red-500">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white font-[Manrope]">Contracts Workflow</h2>
                      <p className="text-xs text-zinc-400">Manage contract process and signatures</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/admin/leads/${leadId}/contracts-workflow`)}
                    className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border border-orange-500/30"
                    data-testid="contracts-workflow-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Open Workflow
                  </Button>
                </div>
                
                {lead?.contracts_workflow ? (
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-white">
                        {lead.contracts_workflow.phases?.filter(p => p.status === "completed").length || 0}
                      </p>
                      <p className="text-xs text-zinc-500">Phases Done</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-orange-400">
                        {lead.contracts_workflow.contract_status?.replace(/_/g, ' ') || "Draft"}
                      </p>
                      <p className="text-xs text-zinc-500">Status</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-white">
                        {lead.contracts_workflow.contract_value || "-"}
                      </p>
                      <p className="text-xs text-zinc-500">Value</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                      <p className={`text-2xl font-bold ${
                        lead.contracts_workflow.health === "on_track" ? "text-green-400" :
                        lead.contracts_workflow.health === "at_risk" ? "text-yellow-400" : "text-red-400"
                      }`}>
                        {lead.contracts_workflow.health === "on_track" ? "🟢" :
                         lead.contracts_workflow.health === "at_risk" ? "🟡" : "🔴"}
                      </p>
                      <p className="text-xs text-zinc-500">Health</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 bg-zinc-900/30 rounded-xl">
                    <p className="text-sm text-zinc-400">Click "Open Workflow" to start contract management</p>
                  </div>
                )}
              </div>
            )}

            {/* Call History Expanded Card */}
            {expandedSections.calls && (
              <div className="bg-[#121212] rounded-2xl border border-purple-500/20 p-6 animate-in slide-in-from-top-2 duration-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-white font-[Manrope] flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-400" />
                    Call History
                  </h2>
                  {callHistory?.last_call_id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API}/admin/retell/sync/${leadId}`, {
                            method: "POST",
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          if (response.ok) {
                            toast.success("Call data synced from Retell");
                            fetchLeadDetails();
                          } else {
                            toast.error("Failed to sync call data");
                          }
                        } catch (error) {
                          toast.error("Sync failed");
                        }
                      }}
                      className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Sync
                    </Button>
                  )}
                </div>
                
                {callHistory && (callHistory.total_calls_made > 0 || callHistory.last_call_id) ? (
                  <>
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <StatCard 
                        icon={PhoneCall} 
                        value={callHistory.total_calls_made || 0} 
                        label="Total Calls" 
                        color="#A855F7"
                        small
                      />
                      <StatCard 
                        icon={Clock} 
                        value={callHistory.last_call_duration ? `${Math.round(callHistory.last_call_duration)}s` : "N/A"} 
                        label="Last Duration" 
                        color="#3B82F6"
                        small
                      />
                      <StatCard 
                        icon={DollarSign} 
                        value={`$${(callHistory.total_call_cost || 0).toFixed(2)}`} 
                        label="Total Cost" 
                        color="#F59E0B"
                        small
                      />
                      <StatCard 
                        icon={callHistory.last_call_successful ? CheckCircle2 : XCircle} 
                        value={callHistory.last_call_successful ? "Yes" : "No"} 
                        label="Last Successful" 
                        color={callHistory.last_call_successful ? "#22C55E" : "#EF4444"}
                        small
                      />
                    </div>
                    
                    {callHistory.last_call_sentiment && (
                      <div className="p-4 bg-[#0a0a0a] rounded-xl border border-white/5 mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-zinc-500">Last Call Sentiment</p>
                          <SentimentBadge sentiment={callHistory.last_call_sentiment} />
                        </div>
                        {callHistory.last_call_summary && (
                          <p className="text-sm text-zinc-300 mt-2">{callHistory.last_call_summary}</p>
                        )}
                      </div>
                    )}
                    
                    {callHistory.last_call_transcript && (
                      <div className="p-4 bg-[#0a0a0a] rounded-xl border border-white/5">
                        <p className="text-xs text-zinc-500 mb-2">Last Call Transcript</p>
                        <p className="text-sm text-zinc-300 whitespace-pre-wrap max-h-40 overflow-y-auto">
                          {callHistory.last_call_transcript || "No transcript available"}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <PhoneCall className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-500">No calls made yet</p>
                    <p className="text-xs text-zinc-600 mt-1">Use the AI Call button above to make a call</p>
                  </div>
                )}
              </div>
            )}

            {/* Email History Expanded Card */}
            {expandedSections.emails && (
              <div className="bg-[#121212] rounded-2xl border border-blue-500/20 p-6 animate-in slide-in-from-top-2 duration-200">
                <h2 className="text-lg font-bold text-white font-[Manrope] mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-400" />
                  Email History
                </h2>
                
                {emailHistory && emailHistory.email_history && emailHistory.email_history.length > 0 ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <StatCard 
                        icon={Mail} 
                        value={emailHistory.total_emails_generated || 0} 
                        label="Total Drafts" 
                        color="#3B82F6"
                        small
                      />
                      <StatCard 
                        icon={Clock} 
                        value={emailHistory.last_email_at ? formatDate(emailHistory.last_email_at) : "N/A"} 
                        label="Last Generated" 
                        color="#3B82F6"
                        small
                      />
                    </div>
                    
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {emailHistory.email_history.slice().reverse().map((email, index) => (
                        <div key={index} className="p-4 bg-[#0a0a0a] rounded-xl border border-white/5">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs text-zinc-500">
                              {formatDate(email.generated_at)} at {formatTime(email.generated_at?.split('T')[1]?.substring(0, 5))}
                            </p>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCopy(email.content)}
                              className="text-zinc-400 hover:text-white h-7 px-2"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-sm text-zinc-300 whitespace-pre-wrap">{email.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Mail className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-500">No emails drafted yet</p>
                    <p className="text-xs text-zinc-600 mt-1">Use the AI Email button above to generate an email</p>
                  </div>
                )}
              </div>
            )}

            {/* Text History Expanded Card */}
            {expandedSections.texts && (
              <div className="bg-[#121212] rounded-2xl border border-green-500/20 p-6 animate-in slide-in-from-top-2 duration-200">
                <h2 className="text-lg font-bold text-white font-[Manrope] mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-green-400" />
                  Text History
                </h2>
                
                {textHistory && textHistory.text_history && textHistory.text_history.length > 0 ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <StatCard 
                        icon={MessageCircle} 
                        value={textHistory.total_texts_generated || 0} 
                        label="Total Drafts" 
                        color="#22C55E"
                        small
                      />
                      <StatCard 
                        icon={Clock} 
                        value={textHistory.last_text_at ? formatDate(textHistory.last_text_at) : "N/A"} 
                        label="Last Generated" 
                        color="#22C55E"
                        small
                      />
                    </div>
                    
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {textHistory.text_history.slice().reverse().map((text, index) => (
                        <div key={index} className="p-4 bg-[#0a0a0a] rounded-xl border border-white/5">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs text-zinc-500">
                              {formatDate(text.generated_at)} at {formatTime(text.generated_at?.split('T')[1]?.substring(0, 5))}
                            </p>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCopy(text.content)}
                              className="text-zinc-400 hover:text-white h-7 px-2"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-sm text-zinc-300 whitespace-pre-wrap">{text.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-500">No text messages drafted yet</p>
                    <p className="text-xs text-zinc-600 mt-1">Use the AI Text button above to generate a message</p>
                  </div>
                )}
              </div>
            )}

            {/* Appointment History Expanded Card */}
            {expandedSections.appointments && (
              <div className="bg-[#121212] rounded-2xl border border-amber-500/20 p-6 animate-in slide-in-from-top-2 duration-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-white font-[Manrope] flex items-center gap-2">
                    <CalendarCheck className="w-5 h-5 text-amber-400" />
                    Appointment History
                  </h2>
                  <Button
                    size="sm"
                    onClick={() => setShowAppointmentForm(true)}
                    className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30"
                  >
                    <CalendarPlus className="w-4 h-4 mr-2" />
                    New Appointment
                  </Button>
                </div>
                
                {/* Create Appointment Form */}
                {showAppointmentForm && (
                  <div className="p-4 bg-[#0a0a0a] rounded-xl border border-amber-500/30 mb-4">
                    <p className="text-sm font-semibold text-amber-400 mb-3">Schedule New Appointment</p>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-xs text-zinc-500 mb-1 block">Date *</label>
                        <input
                          type="date"
                          value={appointmentDate}
                          onChange={(e) => setAppointmentDate(e.target.value)}
                          className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 mb-1 block">Time</label>
                        <select
                          value={appointmentTime}
                          onChange={(e) => setAppointmentTime(e.target.value)}
                          className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                        >
                          <option value="">Select time</option>
                          {/* Generate time slots in 15-minute increments */}
                          {Array.from({ length: 24 * 4 }, (_, i) => {
                            const hour = Math.floor(i / 4);
                            const minute = (i % 4) * 15;
                            const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                            const ampm = hour < 12 ? 'AM' : 'PM';
                            const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                            const timeLabel = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
                            return (
                              <option key={timeValue} value={timeValue}>{timeLabel}</option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="text-xs text-zinc-500 mb-1 block">Notes</label>
                      <input
                        type="text"
                        value={appointmentNotes}
                        onChange={(e) => setAppointmentNotes(e.target.value)}
                        placeholder="Meeting agenda, location, etc."
                        className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setShowAppointmentForm(false);
                          setAppointmentDate("");
                          setAppointmentTime("");
                          setAppointmentNotes("");
                        }}
                        className="text-zinc-400"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveAppointment}
                        disabled={savingAppointment || !appointmentDate}
                        className="bg-amber-500 hover:bg-amber-600 text-black"
                      >
                        {savingAppointment ? <Loader2 className="w-4 h-4 animate-spin" /> : "Schedule"}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Current Appointment */}
                {appointmentHistory?.current_appointment && (
                  <div className="p-4 bg-gradient-to-br from-amber-500/10 to-transparent rounded-xl border border-amber-500/30 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-amber-400 uppercase">Current Appointment</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCancelAppointment(appointmentHistory.current_appointment.index)}
                        disabled={cancellingAppointment === appointmentHistory.current_appointment.index}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                      >
                        {cancellingAppointment === appointmentHistory.current_appointment.index ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <XCircle className="w-3 h-3 mr-1" />
                            Cancel
                          </>
                        )}
                      </Button>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-xl bg-amber-500/20">
                        <Calendar className="w-6 h-6 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-white font-semibold">{formatDate(appointmentHistory.current_appointment.date)}</p>
                        {appointmentHistory.current_appointment.time && (
                          <p className="text-sm text-zinc-400">{formatTime(appointmentHistory.current_appointment.time)}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Appointment History List */}
                {appointmentHistory && appointmentHistory.appointment_history && appointmentHistory.appointment_history.length > 0 ? (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    <p className="text-xs text-zinc-500 uppercase font-semibold">History</p>
                    {appointmentHistory.appointment_history.slice().reverse().map((appt, index) => {
                      const realIndex = appointmentHistory.appointment_history.length - 1 - index;
                      return (
                      <div key={index} className="p-4 bg-[#0a0a0a] rounded-xl border border-white/5">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-amber-400" />
                            <p className="text-sm text-white font-medium">
                              {formatDate(appt.date)} {appt.time && `at ${formatTime(appt.time)}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              appt.status === "scheduled" ? "bg-amber-500/20 text-amber-400" :
                              appt.status === "completed" ? "bg-green-500/20 text-green-400" :
                              appt.status === "cancelled" ? "bg-red-500/20 text-red-400" :
                              "bg-zinc-500/20 text-zinc-400"
                            }`}>
                              {appt.status || "scheduled"}
                            </span>
                            {appt.status === "scheduled" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleCancelAppointment(realIndex)}
                                disabled={cancellingAppointment === realIndex}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-6 w-6 p-0"
                              >
                                {cancellingAppointment === realIndex ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <XCircle className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        {appt.notes && (
                          <p className="text-sm text-zinc-400">{appt.notes}</p>
                        )}
                        <p className="text-xs text-zinc-600 mt-2">
                          Scheduled {formatDate(appt.scheduled_at)} by {appt.scheduled_by}
                        </p>
                      </div>
                    );
                    })}
                  </div>
                ) : (
                  !appointmentHistory?.current_appointment && (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                      <p className="text-zinc-500">No appointments scheduled yet</p>
                      <p className="text-xs text-zinc-600 mt-1">Click "New Appointment" to schedule one</p>
                    </div>
                  )
                )}
              </div>
            )}
          </div>

          {/* Right Column - Pipeline & Appointment */}
          <div className="col-span-4 space-y-6">
            {/* Lead Stats - Moved to right side */}
            <div className="bg-[#121212] rounded-2xl border border-white/5 p-6">
              <h2 className="text-lg font-bold text-white font-[Manrope] mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-cyan-400" />
                Lead Stats
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-[#0a0a0a] rounded-lg">
                  <span className="text-zinc-400 text-sm">Created</span>
                  <span className="text-white text-sm">{formatDate(lead.created_at)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-[#0a0a0a] rounded-lg">
                  <span className="text-zinc-400 text-sm">Last Updated</span>
                  <span className="text-white text-sm">{formatDate(lead.updated_at) || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-[#0a0a0a] rounded-lg">
                  <span className="text-zinc-400 text-sm">Source</span>
                  <span className="text-white text-sm capitalize">{lead.source || "Inbound"}</span>
                </div>
                {lead.conversation_id && (
                  <div className="flex justify-between items-center p-3 bg-[#0a0a0a] rounded-lg">
                    <span className="text-zinc-400 text-sm">Conversation</span>
                    <span className="text-[#14A800] text-sm">Active</span>
                  </div>
                )}
              </div>
            </div>

            {/* Activity History for Sales / Pipeline for others */}
            <div className="bg-[#121212] rounded-2xl border border-white/5 p-6">
              <h2 className="text-lg font-bold text-white font-[Manrope] mb-4 flex items-center gap-2">
                {userRole === "sales" ? (
                  <>
                    <Activity className="w-5 h-5 text-[#14A800]" />
                    Activity History
                  </>
                ) : (
                  <>
                    <TrendingUp className="w-5 h-5 text-[#14A800]" />
                    Pipeline
                  </>
                )}
              </h2>
              
              {userRole === "sales" ? (
                /* Sales View: Live Activity Timeline */
                <div className="space-y-3">
                  {/* Activity Summary Stats */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className="bg-[#0a0a0a] rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-blue-400">{activityTimeline.summary?.total_calls || 0}</p>
                      <p className="text-[10px] text-zinc-500">Calls</p>
                    </div>
                    <div className="bg-[#0a0a0a] rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-purple-400">{activityTimeline.summary?.total_emails || 0}</p>
                      <p className="text-[10px] text-zinc-500">Emails</p>
                    </div>
                    <div className="bg-[#0a0a0a] rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-cyan-400">{activityTimeline.summary?.total_texts || 0}</p>
                      <p className="text-[10px] text-zinc-500">Texts</p>
                    </div>
                    <div className="bg-[#0a0a0a] rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-amber-400">{activityTimeline.summary?.total_appointments || 0}</p>
                      <p className="text-[10px] text-zinc-500">Appts</p>
                    </div>
                  </div>
                  
                  {/* Activity Timeline */}
                  {activityLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                    </div>
                  ) : activityTimeline.activities?.length > 0 ? (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                      {activityTimeline.activities.map((activity, idx) => {
                        const iconMap = {
                          call: <Phone className="w-4 h-4 text-blue-400" />,
                          email: <Mail className="w-4 h-4 text-purple-400" />,
                          text: <MessageSquare className="w-4 h-4 text-cyan-400" />,
                          appointment: <Calendar className="w-4 h-4 text-amber-400" />,
                          created: <UserCheck className="w-4 h-4 text-green-400" />,
                          stage_change: <TrendingUp className="w-4 h-4 text-emerald-400" />
                        };
                        
                        const bgMap = {
                          call: "bg-blue-500/10 border-blue-500/20",
                          email: "bg-purple-500/10 border-purple-500/20",
                          text: "bg-cyan-500/10 border-cyan-500/20",
                          appointment: "bg-amber-500/10 border-amber-500/20",
                          created: "bg-green-500/10 border-green-500/20",
                          stage_change: "bg-emerald-500/10 border-emerald-500/20"
                        };
                        
                        return (
                          <div key={idx} className={`p-3 rounded-lg border ${bgMap[activity.type] || "bg-zinc-500/10 border-zinc-500/20"}`}>
                            <div className="flex items-start gap-3">
                              <div className="p-1.5 rounded-lg bg-[#0a0a0a]">
                                {iconMap[activity.type] || <Activity className="w-4 h-4 text-zinc-400" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">{activity.title}</p>
                                {activity.description && (
                                  <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{activity.description}</p>
                                )}
                                <p className="text-[10px] text-zinc-600 mt-1">
                                  {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : ""}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Activity className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                      <p className="text-sm text-zinc-500">No activity yet</p>
                      <p className="text-xs text-zinc-600 mt-1">Use the AI Actions below to start engaging</p>
                    </div>
                  )}
                  
                  {/* Refresh button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchActivityTimeline}
                    className="w-full mt-2 text-zinc-500 hover:text-white"
                    disabled={activityLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${activityLoading ? "animate-spin" : ""}`} />
                    Refresh Activity
                  </Button>
                  
                  {/* Convert to Opportunity button for Sales Reps when lead is in Lead stage */}
                  {lead.stage === "Lead" && (
                    <Button
                      onClick={() => handleStageChange("Opportunity")}
                      className="w-full mt-4 bg-[#14A800] hover:bg-[#14A800]/90 text-black font-semibold"
                      data-testid="convert-to-opportunity-btn"
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Convert to Opportunity
                    </Button>
                  )}
                </div>
              ) : (
                /* Admin/PM View: Clickable pipeline stages */
                <div className="space-y-3">
                  {(userRole === "product_manager" 
                    ? ["Opportunity", "Contracts & Payments", "Implementation"]
                    : ["Lead", "Opportunity", "Contracts & Payments", "Implementation"]
                  ).map((stage, index, arr) => {
                    const isActive = lead.stage === stage;
                    const stageOrder = arr;
                    const isPast = stageOrder.indexOf(lead.stage) > index;
                    const style = getStageStyle(stage);
                    
                    return (
                      <button
                        key={stage}
                        onClick={() => handleStageChange(stage)}
                        className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${
                          isActive 
                            ? `${style.bg} ${style.border} border-2`
                            : isPast
                            ? "bg-white/5 border-white/10"
                            : "bg-[#0a0a0a] border-white/5 hover:border-white/10"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{style.icon}</span>
                          <span className={`font-medium ${isActive ? style.text : "text-zinc-400"}`}>
                            {stage === "Contracts & Payments" ? "Contracts" : stage}
                          </span>
                        </div>
                        {isActive && <CheckCircle2 className={`w-5 h-5 ${style.text}`} />}
                        {isPast && <Check className="w-5 h-5 text-zinc-500" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Quick appointment widget - simplified since history is in left column */}
            <div className="bg-[#121212] rounded-2xl border border-white/5 p-6">
              <h2 className="text-lg font-bold text-white font-[Manrope] mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-400" />
                Next Appointment
              </h2>
              
              {hasAppointment ? (
                <div className="p-4 bg-gradient-to-br from-amber-500/10 to-transparent rounded-xl border border-amber-500/20">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-amber-500/20">
                      <CalendarCheck className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{formatDate(lead.appointment_date)}</p>
                      {lead.appointment_time && (
                        <p className="text-sm text-zinc-400">{formatTime(lead.appointment_time)}</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setExpandedSections(prev => ({ ...prev, appointments: true }));
                    setShowAppointmentForm(true);
                  }}
                  className="w-full p-4 border-2 border-dashed border-white/10 rounded-xl text-zinc-500 hover:text-white hover:border-amber-500/30 transition-all"
                >
                  <Calendar className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-sm">Schedule Appointment</p>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Appointment Reminder Popup */}
      {showAppointmentReminder && lead?.appointment_date && (
        <div className="fixed bottom-6 right-6 max-w-sm bg-amber-500/10 backdrop-blur-xl border border-amber-500/30 rounded-2xl p-4 shadow-2xl animate-in slide-in-from-bottom-4 duration-300 z-50">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl bg-amber-500/20">
              <Bell className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-amber-400 mb-1">Upcoming Appointment</p>
              <p className="text-sm text-white">
                {lead.first_name} {lead.last_name}
              </p>
              <p className="text-sm text-zinc-300">
                {formatDate(lead.appointment_date)}
                {lead.appointment_time && ` at ${formatTime(lead.appointment_time)}`}
              </p>
              {(() => {
                const appointmentDateTime = new Date(lead.appointment_date);
                const now = new Date();
                const daysUntil = Math.ceil((appointmentDateTime - now) / (1000 * 60 * 60 * 24));
                return (
                  <p className={`text-xs mt-1 ${daysUntil <= 1 ? "text-red-400" : daysUntil <= 3 ? "text-amber-400" : "text-zinc-500"}`}>
                    {daysUntil === 0 ? "Today!" : daysUntil === 1 ? "Tomorrow!" : `In ${daysUntil} days`}
                  </p>
                );
              })()}
            </div>
            <button
              onClick={() => {
                setShowAppointmentReminder(false);
                setAppointmentReminderDismissed(true);
              }}
              className="p-1 text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
      
      {/* Edit Lead Modal */}
      <LeadFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={fetchLeadDetails}
        lead={lead}
        mode="edit"
      />
    </div>
  );
}
